import axios from "axios";
import { useSnack } from "../providers/SnackBarProvider";
import { useEffect, useState } from "react";
import { useUser } from "../users/providers/UserProvider.jsx";
import { removeToken } from "../users/service/localStorage";
import {
  extractError,
  getHttpStatus,
  isAppUpdateRequired,
  isRevokedSession,
} from "../utils/apiErrorUtils";
import { DEFAULT_TIMEOUT_MS } from "../utils/axiosTimeouts";
import { holdRefusal } from "../utils/refusalNotice";
import { isNativePlatform } from "../lib/nativeAuth";

/**
 * Give every request a deadline (#214). axios ships with `timeout: 0` — wait
 * forever — so a half-open connection produced no error, no snackbar and no
 * settled promise, just a spinner that never stopped.
 *
 * At MODULE scope, not inside the effect below. Effects run after the first
 * commit, so a deadline installed there would be missed by every request fired
 * during the first render — the auth bridge and the two badge polls all start
 * around then. This line runs while index.js is still walking its import graph
 * (index → App → ApiInterceptor → here), before `root.render`, so there is no
 * request in the app's lifetime it can miss.
 *
 * Here rather than in a module of its own because this file already owns
 * axios's global defaults for the app: the auth header is set on the same
 * object a few lines down.
 */
axios.defaults.timeout = DEFAULT_TIMEOUT_MS;

/**
 * Q-31: tell the API which build is asking ("1.3.2+9" — the number after the
 * plus is the Android versionCode), so it can answer a build that is too old
 * with 426 APP_UPDATE_REQUIRED (server/middleware/appVersionGate.js). At module
 * scope for the same reason as the deadline above: no request may miss it.
 * `window.Capacitor` is already there by then, because the native shell injects
 * its bridge into <head> ahead of the bundle.
 *
 * The packaged app only. The update screen's one way out is the Play Store, so
 * the web app does not send it and the gate never applies to it.
 */
if (process.env.REACT_APP_VERSION && isNativePlatform()) {
  axios.defaults.headers.common["X-App-Version"] = process.env.REACT_APP_VERSION;
}

/**
 * Returns true once the API has said this build is too old (Q-31). It stays
 * true: nothing this build does can make it new enough, and the way past it is
 * an update, which restarts the app.
 */
const useAxios = () => {
  const snack = useSnack();
  const { token, setToken, setUser } = useUser();
  const [updateRequired, setUpdateRequired] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["x-auth-token"] = token;

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // [B-63] A request the app aborted on purpose (the listing sheet closed
        // mid-upload) is not a network failure: no snackbar, and the cancel
        // passes through unchanged so the caller can tell it apart.
        if (axios.isCancel(error)) return Promise.reject(error);

        const status = getHttpStatus(error);
        const message = extractError(error, "An error occurred");

        // Q-31: this build is too old for the API. The update screen takes
        // over (ApiInterceptor renders it), so no toast on top of it, and the
        // session is left alone: the account is fine, only the build is not.
        if (isAppUpdateRequired(error)) {
          setUpdateRequired(true);
          error.userMessage = message;
          error.httpStatus = status;
          return Promise.reject(error);
        }

        // The account was suspended or deleted while this session was open.
        // Retrying can never succeed, so end the session here rather than
        // letting every poll produce another error snackbar. The server's
        // message says why, so the user is not just silently ejected.
        if (isRevokedSession(error)) {
          removeToken();
          setToken(null);
          setUser(null);
          snack("error", message);
          const enhancedError = error?.response ? error : new Error(message);
          enhancedError.userMessage = message;
          enhancedError.httpStatus = status;
          return Promise.reject(enhancedError);
        }

        const shouldShowSnack =
          status === 401 ||
          status === 403 ||
          (status && status >= 500) ||
          !error.response;

        // `quiet: true` on the request config marks a background reload: a
        // retry on each poll answer, or a refresh on app return. Its caller
        // already shows the failure in place (or keeps what is on screen), so
        // a toast here would repeat every 30 seconds on every screen (B-61).
        //
        // `quietForbidden: true` is narrower: only a 403 is quiet. The map's
        // discovery reads use it because the screen turns that 403 (the
        // launch-area refusal) into its own message; an outage on the same
        // read must still be said, since for a new account the map shows
        // "Nothing to match on yet" and nothing else (release review of
        // E2E audit 15·9c). Revoked sessions are handled above, never here.
        const quiet = error.config?.quiet || (status === 403 && error.config?.quietForbidden);
        if (shouldShowSnack) {
          if (!quiet) snack("error", message);
        } else if (
          !quiet &&
          // A write nobody tapped (mark read, the map's location) passes
          // `quietRefusal: true`: its refusal is not news, but an outage
          // above is still raised for it, as it always was.
          !error.config?.quietRefusal &&
          status >= 400 &&
          status < 500 &&
          error.config?.method !== "get"
        ) {
          // [B-31] Any other 4xx on an action is a refusal, and it is shown
          // unless the screen that asked shows it itself (utils/refusalNotice).
          // A load (GET) is left to its screen's own empty and error states.
          // When the server gave no sentence (axios's own "Request failed with
          // status code 400", or a proxy's HTML page), say it plainly instead.
          const said = message !== error.message && !/^\s*</.test(message);
          holdRefusal(() => snack("error", said ? message : "That didn't work."));
        }

        const enhancedError = error?.response ? error : new Error(message);
        enhancedError.userMessage = message;
        enhancedError.httpStatus = status;
        return Promise.reject(enhancedError);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token, snack, setToken, setUser]);

  return updateRequired;
};

export default useAxios;
